// Sentry -> GlitchTip error tracking for the web wallet, for a signed-in user only.
//
// The DSN is not in this public bundle. Anyone holding one can post crash events,
// and those events can reach AI agents, so a logged-out visitor must not receive it
// (owner ruling 2026-11-01, developerz.ai#7785). `AuthProvider` calls `start` once
// a session exists, which fetches the DSN from the authenticated API. Logged-out
// errors are not reported, and neither is anything before that fetch returns.
//
// The web wallet is Solid, so this uses `@sentry/solid`, the framework's SDK.
import * as Sentry from "@sentry/solid";
import { apiBaseUrl } from "./sdk/client.ts";

export interface ErrorReportingDeps {
  fetch: (url: string, init: RequestInit) => Promise<Response>;
  init: (options: { dsn: string; environment?: string; tracesSampleRate: number }) => void;
  baseUrl: string;
  environment?: string;
}

export function createErrorReporting(deps: ErrorReportingDeps) {
  let started = false;
  return {
    /** Never rejects: a failed lookup leaves reporting off and the wallet unchanged. */
    async start(jwt: string): Promise<void> {
      if (started) return;
      started = true;
      try {
        const res = await deps.fetch(`${deps.baseUrl}/me/error-reporting`, {
          headers: { authorization: `Bearer ${jwt}` },
        });
        const dsn = res.ok ? ((await res.json()) as { dsn: string | null }).dsn : null;
        if (dsn) {
          // Error tracking only, no performance tracing.
          deps.init({ dsn, environment: deps.environment, tracesSampleRate: 0 });
          return;
        }
      } catch {
        // Offline or a bad body: leave reporting off and allow a later retry.
      }
      started = false;
    },
  };
}

export const errorReporting = createErrorReporting({
  fetch: (url, init) => fetch(url, init),
  init: Sentry.init,
  baseUrl: apiBaseUrl(),
  environment: import.meta.env.MODE,
});
